import React from 'react';
import { useDispatch } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';

import { useErrorToaster } from '../../utils/errorToaster';
import { saveDriverDetails } from '../../store/actions/account';
import DateInput from '../shared/DateInput/DateInput';

function DriverDetails() {
  const { t } = useTranslation(['account', 'home', 'common', 'validation']);
  const dispatch = useDispatch();
  const displayError = useErrorToaster();

  const initialValues = {
    licenceNumber: '',
    licenseLocation: '',
    licenseObtainingOn: '',
    licenceExpirationTime: ''
  };

  const validationSchema = Yup.object().shape({
    licenceNumber: Yup.string().required(t('validation:required')),
    licenseLocation: Yup.string().required(t('validation:required')),
    licenseObtainingOn: Yup.string()
      .validDate(t('validation:date-invalid'))
      .required(t('validation:required'))
  });

  const handleFormSubmit = async (
    {
      licenceNumber,
      licenseLocation,
      licenseObtainingOn,
      licenceExpirationTime
    },
    { setSubmitting }
  ) => {
    try {
      await dispatch(
        saveDriverDetails({
          licenceNumber,
          licenseLocation,
          licenseObtainingOn,
          licenceExpirationTime
        })
      );
    } catch (error) {
      setSubmitting(false);
      displayError(error);
    }
  };

  return (
    <div className="driver-details">
      <Typography component="h1" className="driver-details">
        {t('account:driver-details')}
      </Typography>

      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleFormSubmit}
      >
        {({ values, handleChange, isSubmitting, errors, touched }) => (
          <Form>
            <div className="form-group">
              <TextField
                name="licenceNumber"
                label={t('account:license-number')}
                variant="outlined"
                margin="none"
                value={values.licenceNumber}
                onChange={handleChange}
                error={touched.licenceNumber && !!errors.licenceNumber}
                helperText={
                  touched.licenceNumber && errors.licenceNumber
                    ? errors.licenceNumber
                    : null
                }
                fullWidth
              />
            </div>

            <div className="form-group">
              <TextField
                name="licenseLocation"
                label={t('account:obtaining-location')}
                variant="outlined"
                margin="none"
                value={values.licenseLocation}
                onChange={handleChange}
                error={touched.licenseLocation && !!errors.licenseLocation}
                helperText={
                  touched.licenseLocation && errors.licenseLocation
                    ? errors.licenseLocation
                    : null
                }
                fullWidth
              />
            </div>

            <div className="form-group">
              <DateInput
                name="licenseObtainingOn"
                placeholder={t('account:obtaining-date')}
                value={values.licenseObtainingOn}
                onChange={handleChange}
                error={
                  touched.licenseObtainingOn && !!errors.licenseObtainingOn
                }
                helperText={
                  touched.licenseObtainingOn && errors.licenseObtainingOn
                    ? errors.licenseObtainingOn
                    : null
                }
              />
            </div>

            <div className="form-group">
              <DateInput
                name="licenceExpirationTime"
                placeholder={t('account:expiration-date')}
                value={values.licenceExpirationTime}
                onChange={handleChange}
                error={
                  touched.licenceExpirationTime &&
                  !!errors.licenceExpirationTime
                }
                helperText={
                  touched.licenceExpirationTime && errors.licenceExpirationTime
                    ? errors.licenceExpirationTime
                    : null
                }
              />
            </div>

            <div className="button-group">
              <Button
                type="submit"
                fullWidth
                disabled={isSubmitting}
                variant="contained"
                color="primary"
              >
                {t('common:next')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default DriverDetails;
